import { ref } from "@vue/composition-api";
import axios from "@/libs/axios";
import axiosV1 from "@/libs/axiosV1";
import ChannelInterface from "@/interfaces/channel.interface";
import TeamInterface from "@/interfaces/team.interface";
import UserLimitsInterface from "@/interfaces/userLimits.interface";
import { BvModal } from "bootstrap-vue";
// @ts-ignore
import { useRouter } from "@/@core/utils/utils";
import { BillingPeriodMonthsType } from "@/types/billingMonths.type";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
// @ts-ignore
import i18n from "@/libs/i18n";

const limits = ref<UserLimitsInterface>();
const isLoadingLimits = ref(true);

export default function useUsers() {
    const toast = useToast();

    const channels = ref<Array<ChannelInterface & { text: string | undefined; value: number }>>([]);
    const teams = ref<Array<TeamInterface & { text: string; value: number }>>([]);
    const billingMonths = ref<BillingPeriodMonthsType>();
    const prices = ref<Record<BillingPeriodMonthsType, number>>();
    const { router } = useRouter();

    const fetchLimits = (refresh?: boolean): Promise<UserLimitsInterface> => {
        if (!refresh && limits.value) {
            return Promise.resolve(limits.value);
        }

        isLoadingLimits.value = true;

        return axios.get("users/limits").then((response) => {
            limits.value = response.data;
            isLoadingLimits.value = false;
            return response.data;
        });
    };

    const increaseLimits = (limits: { tosAgree: boolean; newUsers: number }): Promise<{ success: boolean }> =>
        axiosV1
            .post(`${(process.env.VUE_APP_API_GATEWAY ?? "").replace("v1", "V2")}/users/increase-limit`, limits)
            .then((response) => {
                if (!response.data.success) {
                    throw new Error();
                }

                toast(
                    {
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.users.limits.notification.successful-increase-title"),
                            text: i18n.t("settings.users.limits.notification.successful-increase-text"),
                            icon: "check",
                            variant: "success",
                        },
                    },
                    {
                        timeout: false,
                    }
                );

                return response.data;
            })
            .catch(() => {
                toast(
                    {
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.users.limits.notification.failed-increase-title"),
                            text: i18n.t("settings.users.limits.notification.failed-increase-text"),
                            icon: "times",
                            variant: "danger",
                        },
                    },
                    {
                        timeout: false,
                    }
                );

                return { success: false };
            });

    const requestIncreaseLimits = (limits: { newUsers: number }): Promise<{ success: boolean }> =>
        axiosV1
            .post(`${(process.env.VUE_APP_API_GATEWAY ?? "").replace("v1", "V2")}/users/request-increase-limit`, limits)
            .then((response) => {
                if (!response.data.success) {
                    throw new Error();
                }

                toast(
                    {
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.users.limits.notification.successful-request-title"),
                            text: i18n.t("settings.users.limits.notification.successful-request-text"),
                            icon: "check",
                            variant: "success",
                        },
                    },
                    {
                        timeout: false,
                    }
                );

                return response.data;
            })
            .catch(() => {
                toast(
                    {
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.users.limits.notification.failed-request-title"),
                            text: i18n.t("settings.users.limits.notification.failed-request-text"),
                            icon: "times",
                            variant: "danger",
                        },
                    },
                    {
                        timeout: false,
                    }
                );

                return { success: false };
            });

    const fetchPrices = () =>
        axios.get("users/pricing").then((response) => {
            billingMonths.value = response.data.billingMonths;
            prices.value = response.data.prices;

            return response.data;
        });

    const fetchChannels = () =>
        axios
            .get<ChannelInterface[]>("channels", {
                params: {
                    join: "userChannels",
                    sort: "name,ASC",
                },
            })
            .then((response) => {
                channels.value = response.data.map((channel) => ({
                    ...channel,
                    text: channel.name,
                    value: channel.id,
                }));

                return channels.value;
            });

    const fetchTeams = () =>
        axios
            .get<TeamInterface[]>("teams", {
                params: {
                    join: "teamUsers",
                    sort: "name,ASC",
                },
            })
            .then((response) => {
                teams.value = response.data.map((team) => ({
                    ...team,
                    text: team.name,
                    value: team.id,
                }));

                return teams.value;
            });

    const sendInviteEmail = (emailAddress: string): unknown => {
        // NOTE: We don't want a mail endpoint in the public API. And with time concerns, we've decided to directly use the API gateway for now.
        return axiosV1.post(`users/re-send-invite-email/to-email/${emailAddress}`);
    };

    // The ExtraUserRequestModal component needs to be loaded while calling this function
    const addUserOrOpenRequestModal = async (bvModel: BvModal): Promise<void> => {
        if (!limits.value) {
            await fetchLimits();
        }

        if (limits.value?.isExceeded) {
            bvModel.show("extra-users-request-modal");
            return;
        }

        router.push("/settings/users/add");
    };

    return {
        teams,
        channels,
        limits,
        prices,
        billingMonths,
        isLoadingLimits,
        increaseLimits,
        requestIncreaseLimits,
        addUserOrOpenRequestModal,
        fetchPrices,
        sendInviteEmail,
        fetchLimits,
        fetchChannels,
        fetchTeams,
    };
}
